import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, output, signal, ViewChild } from '@angular/core';
import { Html5Qrcode } from 'html5-qrcode';
import { HeaderModule } from "../header/header.module";
import { Router } from '@angular/router';
import { dashboardRoute } from '../route-names';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ApiService, verifyWithOrgAPI } from '../api/api.service';
import { ElectorDataService } from '../Services/elector-data.service';
import { DialogService, DialogType } from '../Services/dialog.service';
import { KeyValuePair } from '../backend-types/misc-types';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ElectorAndBallot } from '../ballot/backend-types';
import { BallotListService } from '../ballot-list/ballot-list.service';
import { BackButtonConfirmationModalModule } from '../back-button-confirmation-modal/back-button-confirmation-modal.module';

@Component({
    selector: 'app-qr-scanner',
    imports: [HeaderModule, MatProgressSpinnerModule, BackButtonConfirmationModalModule],
    templateUrl: './qr-scanner.component.html',
    styleUrl: './qr-scanner.component.scss'
})
export class QrScannerComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('qrCodeReader') qrCodeReader!: ElementRef;
  scanResult: string[] = [];
  html5QrCode!: Html5Qrcode;
  router = inject(Router)
  isMobileScreen  = false
  qrCodeDetected = output<string>();
  displaySpinner: boolean = false;
  dialogTitle: string = "";
  dialogMessage: string = "";
  cameraError = signal<string | null>(null);  
  cameraHasError = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private apiService: ApiService,
    readonly userService: ElectorDataService,
    private dialogService: DialogService,
    private readonly dialog: MatDialog,
    private readonly ballotListService: BallotListService
  ) { }

  ngOnInit() {
    this.breakpointObserver.observe('(max-width: 720px)').subscribe(result => {
      const newScreenState = result.matches;
      
      if (newScreenState !== this.isMobileScreen) {
        this.isMobileScreen = newScreenState;
        this.restartScanner(); 
      }
    });
  }

  ngAfterViewInit() {
    if(!this.userService.isOrgVerificationAllowed){
      this.dialogMessage = $localize`You have no organization verification attempts remaining.<br><br>You may attempt to verify with an organization again in ${this.userService.orgVerifyLockoutTimeRemainingString()}.`;
      this.dialogTitle = $localize`Please Wait.`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
      this.router.navigate(dashboardRoute);
      return;
    }
    this.html5QrCode = new Html5Qrcode('qr-reader');
    this.startScanner();
  }

  startScanner() {
    let qrWidth = 250
    let qrHeight = 250
    if(this.isMobileScreen) {
      qrWidth = 150
      qrHeight = 150
    }
    else {
      qrWidth = 250
      qrHeight = 250
    }
    const config = {
      fps: 10,
      qrbox: { width: qrWidth, height:qrHeight },
    };

    this.html5QrCode
      .start(
        { facingMode: 'environment' }, 
        config,
        this.onScanSuccess.bind(this),
        () => {}
      )
      .catch((err) => {
        this.cameraHasError = true
        const errorStr = err?.toString().toLowerCase() || '';
        if (errorStr.includes('permission') || errorStr.includes('denied')) {
          this.cameraError.set('permission-denied'); 
        } else if (errorStr.includes('not found') || errorStr.includes('no camera')) {
          this.cameraError.set('no-camera');
        } else {
          this.cameraError.set('generic');
        }
    
        console.error('Error starting the scanner:', err);
        this.displaySpinner = false;
      });
  }

  async onScanSuccess(decodedText: string, decodedResult: any) {
    await this.html5QrCode.stop();
    if(!this.userService.isOrgVerificationAllowed){
      this.dialogMessage = $localize`You have no attempts remaining.<br><br>You may attempt to verify with an organization again in ${this.userService.orgVerifyLockoutTimeRemainingString()}.`;
      this.dialogTitle = $localize`Please Wait.`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
      this.restartScanner();
      this.router.navigate(dashboardRoute);
      return;
    }
    let tag = decodedText.substring(0,2);
    if(tag !== "-4") {
      this.dialogMessage = $localize`This QR Code is formatted incorrectly.`;
      this.proccessOrgVerifyRemainingAttempts();
      this.dialogTitle = $localize`QR Code Error`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
      this.restartScanner();
      return;
    }
    this.scanResult = decodedText.substring(2).split("|");
    let len = this.scanResult.length;
    if(len < 2){
      this.dialogMessage = $localize`This QR Code is formatted incorrectly.`;
      this.proccessOrgVerifyRemainingAttempts();
      this.dialogTitle = $localize`QR Code Error`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
      this.restartScanner();
      return;
    }
    this.displaySpinner = true;

    let data: KeyValuePair[] = [];
    for (let i = 0; i < len-1; i++){
      data.push({key: i.toString(), value: this.scanResult[i]});
    }
    this.apiService.postWithHandshakeCheck<ElectorAndBallot>(verifyWithOrgAPI,
      {
        data: data,
        orgKey: +this.scanResult[len-1]
      }).subscribe({
        next: (resp) => {
          this.displaySpinner = false;
          if(resp.body?.orgVerifications){
            let sortedOrgs = resp.body.orgVerifications?.sort((a, b) => (new Date(b.oidvTimestamp ?? 0).getTime() - new Date(a.oidvTimestamp ?? 0).getTime()));
            if(sortedOrgs && sortedOrgs.length > 0) {
              let mostRecentOrgVerification = sortedOrgs[0];
              this.userService.addOrgVerification(mostRecentOrgVerification);

              if(resp.body.elector?.idv_status && resp.body.elector.idv_status_timestamp) {
                this.userService.idvStatus.set(resp.body.elector.idv_status);
                this.userService.idvTimestamp.set(resp.body.elector.idv_status_timestamp);
              }
              this.ballotListService.setBallots(resp.body);
              this.userService.resetOrgVerifyAttempts();
              this.dialogTitle = $localize`Success!`;
              this.dialogMessage = $localize`You have been successfully verified by ${mostRecentOrgVerification.organizationName}.`;
              this.dialogService.openDialog(DialogType.SUCCESS, this.dialogTitle, this.dialogMessage);
          
              setTimeout(() => {
                this.dialog.closeAll();
                this.router.navigate(dashboardRoute);
              }, 2000);
            }
          } else {
            
            this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
            this.proccessOrgVerifyRemainingAttempts();
            this.dialogTitle = $localize`Unknown Error`;
            this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
          }
          this.router.navigate(dashboardRoute);
        },
        error: (err) => {
          this.displaySpinner = false;
          if ((err.error_type !== undefined && err.error_type === 'INVALID_IDENTITY') ||
            (err.error !== undefined && err.error.error_type === 'INVALID_IDENTITY')) {
            this.dialogTitle = $localize`Information not found`
            this.dialogMessage = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the <b>"Contact Support"</b> link for assistance.`;
            this.proccessOrgVerifyRemainingAttempts();
            this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
          } else if ((err.error_type !== undefined && err.error_type === 'INVALID_API') ||
          (err.error !== undefined && err.error.error_type === 'INVALID_API')) {
            this.dialogMessage= $localize`The API key is invalid. Please use the "Contact Support" link for assistance.`;
            this.proccessOrgVerifyRemainingAttempts();
            this.dialogTitle = $localize`Invalid API Key`;
            this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogMessage);
          } else  if ((err.error_type !== undefined && err.error_type === 'ALREADY_REGISTERED') ||
            (err.error !== undefined && err.error.error_type === 'ALREADY_REGISTERED')) {
            this.dialogTitle = $localize`Already verified`
            this.dialogMessage = $localize`The supplied verification data has already been used by another user to be verified by this organization. If you believe this is in error, use the <b>"Contact Support"</b> link for assistance.`;
            this.proccessOrgVerifyRemainingAttempts();
            this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
          } else if ((err.error_type !== undefined && err.error_type === 'INVALID_FORM') ||
          (err.error !== undefined && err.error.error_type === 'INVALID_FORM')) {
            this.dialogMessage= $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
            this.proccessOrgVerifyRemainingAttempts();
            this.dialogTitle = $localize`Malformed Data`
            this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogMessage);
          } else if ((err.error_type !== undefined && err.error_type === 'VERIFY_ORG_LOCKED') ||
          (err.error !== undefined && err.error.error_type === 'VERIFY_ORG_LOCKED')) {
            this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
            this.userService.orgVerifyAttemptsRemaining = 1;
            this.proccessOrgVerifyRemainingAttempts();
            this.dialogTitle = $localize`Too Many Attempts`;
            this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
          } else if ((err.error_type !== undefined && (err.error_type === 'DECODER_STATE_LOST' || err.error_type === 'INVALID_MTE_DECODING')) ||
          (err.error !== undefined && (err.error.error_type === 'DECODER_STATE_LOST' || err.error.error_type === 'INVALID_MTE_DECODING' ))) {
            this.dialogMessage = $localize`We could not process your request. Please log out, log in again, and try again.`;
            this.dialogTitle = $localize`Error.`
            this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogMessage);
          } else {
            this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
            this.proccessOrgVerifyRemainingAttempts();
            this.dialogTitle = $localize`Unknown Error`;
            this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
          }
        }
      })
  }

  ngOnDestroy() {
    if (this.html5QrCode && this.html5QrCode.isScanning) {
      this.html5QrCode
        .stop()
        .catch((err) => {
          console.error('Error stopping scanner :', err);
        });
    }
  }

  previous(){
    this.router.navigate(dashboardRoute)
  }

  async restartScanner() {
    if (this.html5QrCode && this.html5QrCode.isScanning) {
      await this.html5QrCode.stop().catch(err => console.error('Error stopping scanner:', err));
    }
    this.startScanner();
  }

  retryScanner() {
    this.cameraError.set(null);      
    this.cameraHasError = false;
    this.startScanner();            
  }

  proccessOrgVerifyRemainingAttempts() {
    this.userService.dereaseOrgVerifyAttemptsRemaining();
    if(this.userService.orgVerifyAttemptsRemaining === 1){
      this.dialogMessage += $localize`<br><br>You have 1 attempt remaining.`;
    } else if (this.userService.orgVerifyAttemptsRemaining === 0){
      this.dialogMessage += $localize`<br><br>You have no attempts remaining.<br><br>You may attempt to verify with an organization again in 15 minutes.`;
      this.userService.startOrgVerifyLockoutTimer();
      setTimeout(()=>{
        this.router.navigate(dashboardRoute);
      }, 3000);
    } else {
      this.dialogMessage += $localize`<br><br>You have ${this.userService.orgVerifyAttemptsRemaining} attempts remaining.`;
    }
  }
}


